class SearchFilter {
    constructor(field, value, fieldOperator) {
        this.field = field;
        this.fieldOperator = fieldOperator || '=';
        this.value = value;
    }
}

// the parameters whose values are comma delimited strings
const commaDelimitedParameters = ['category', 'resolutionReasons', 'ruleId'];

class SearchFiltersConverter {

    /**
     * Converts original student list parameter format to search filters format.
     *
     * @param {object} studentListParameters - the student list parameters to be converted
     * @returns {array} search filters - the search filters converted from the given parameters
     */
    toSearchFilters = (studentListParameters) => {

        let searchFilters = [];
        let andSearchFilters = [];

        for (let param in studentListParameters) {

            let paramValue = commaDelimitedParameters.includes(param)
                ? studentListParameters[param].split(',')
                : studentListParameters[param];

            // For parameters whose values are represented as a comma delimited list or an array,
            // the values in that comma delimited list should be treated as one 'OR' condition.
            // The 'attributes' parameter is the only array that should be treated as an 'AND' condition.
            if (Array.isArray(paramValue)) {
                searchFilters.push({
                    operator: param === 'attributes' ? 'AND' : 'OR',
                    filterFields: paramValue.map((val) => new SearchFilter(param, val))
                });
            } else {
                andSearchFilters.push(new SearchFilter(param, paramValue));
            }
        }

        if (andSearchFilters.length > 0) {
            searchFilters.push({
                operator: 'AND',
                filterFields: andSearchFilters
            });
        }

        return searchFilters;

    }

    /**
     * If this parameter is represented as a comma delimited string in the old student list
     * parameter format, convert accordingly.
     *
     * @param {object} studentListParameters - the already-converted student list parameters
     * @param {string} field - the comma delimited field to be converted
     * @param {string} value - the value to add to the comma delimited list
     */
    toStudentListCommaDelimitedParameter = (studentListParameters, field, value) => {
        if (studentListParameters.hasOwnProperty(field)) {
            // append to comma delimited string
            studentListParameters[field] += `,${value}`;
        } else {
            // initialize comma delimited list w/ first value
            studentListParameters[field] = value;
        }
    }

    /**
     * If this parameter is represented as an array in the old student list
     * parameter format, convert accordingly.
     *
     * @param {object} studentListParameters - the already-converted student list parameters
     * @param {string} field - the array field to be converted
     * @param {string} value - the value to add to the array
     */
    toStudentListArrayParameter = (studentListParameters, field, value) => {
        if (studentListParameters.hasOwnProperty(field)) {
            // add value to array
            studentListParameters[field].push(value);
        } else {
            // initialize array w/ first value
            studentListParameters[field] = [value];
        }
    }

    /**
     * Converts search filters to original student list parameter format
     *
     * @param {array} searchFilters - an array of search filters to be converted to student list parameters
     * @returns {object} an object representing the converted student list parameters
     */
    toStudentListParameters = (searchFilters) => {

        // the student list parameters to be returned
        let studentListParameters = {};

        for (let filterConditionIndex in searchFilters) {

            // get each filter condition in the search filters
            let {filterFields, operator} = searchFilters[filterConditionIndex];

            for (let filterFieldIndex in filterFields) {

                // get each field and it's value in the current filter condition
                let {field, value} = filterFields[filterFieldIndex];

                if (operator === 'OR' || field === 'attributes') {
                    // The fields in OR conditions are represented as arrays or comma delimited strings in the
                    // old student list parameter format. The only exception to this is the 'attributes' parameter, which
                    // is represented as an array in the old format, despite being considered an AND condition in
                    // the new search filters format.
                    if (commaDelimitedParameters.includes(field)) {
                        this.toStudentListCommaDelimitedParameter(studentListParameters, field, value);
                    } else {
                        this.toStudentListArrayParameter(studentListParameters, field, value);
                    }
                } else if (operator === 'AND') {
                    // fields in AND conditions are mapped directly to their values in the old student
                    // list parameter format
                    studentListParameters[field] = value;
                } else {
                    // do nothing, NOT AND & NOT OR operators are not supported in the old student list
                    // parameter format
                }
            }
        }

        return studentListParameters;

    }
}

export default new SearchFiltersConverter();
